import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Tabs, Button, Typography, Space, Tooltip, Badge, Drawer, List, Tag, Dropdown, Modal, Form, Input } from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  LogoutOutlined,
  BookOutlined,
  CalendarOutlined,
  DatabaseOutlined,
  HistoryOutlined,
  TeamOutlined,
  CloudServerOutlined,
  CloudOutlined,
  InboxOutlined,
  QuestionCircleOutlined,
  MenuOutlined,
  DesktopOutlined,
  UserOutlined,
  SafetyOutlined,
  SafetyCertificateOutlined,
  CodeOutlined,
  BellOutlined,
  WarningOutlined,
  CarOutlined,
  KeyOutlined,
  QrcodeOutlined,
  NotificationOutlined,
  BugOutlined,
  RobotOutlined,
  MessageOutlined,
  ExperimentOutlined,
  WifiOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { brand, gray, font, layout } from '../theme/tokens';
import { activityLogAPI, dashboardAPI, authMeAPI, systemAlertAPI } from '../services/api';
import { usePermission } from '../contexts/PermissionContext';
import { useIsMobile } from '../hooks/useIsMobile';
import DashboardPage from '../pages/DashboardPage';
import ProtocolListPage from '../pages/ProtocolListPage';
import TestMaterialPage from '../pages/TestMaterialPage';
import GlossaryPage from '../pages/GlossaryPage';
import ProtocolFormPage from '../pages/ProtocolFormPage';
import SchedulePage from '../pages/SchedulePage';
import MasterPage from '../pages/MasterPage';
import SystemGuidePage from '../pages/SystemGuidePage';
import TapoManagementPage from "../pages/TapoManagementPage";
import ActivityLogPage from '../pages/ActivityLogPage';
import PartnerAccountPage from '../pages/PartnerAccountPage';
import MonitoringPage from '../pages/MonitoringPage';
import RegistryPage from '../pages/RegistryPage';
import StoragePage from '../pages/StoragePage';
import ServerLogPage from '../pages/ServerLogPage';
import UserManagementPage from '../pages/UserManagementPage';
import RoleManagementPage from '../pages/RoleManagementPage';
import TerminalPage from '../pages/TerminalPage';
import ChatBot from './ChatBot';
import GlobalSearch from './GlobalSearch';
import BusinessTripPage from '../pages/BusinessTripPage';
import QRManagementPage from '../pages/QRManagementPage';
import NcpmsPage from '../pages/NcpmsPage';
import BoardPage from '../pages/BoardPage';
import AdbDemoPage from '../pages/AdbDemoPage';
import InspectionPage from '../pages/InspectionPage';
import InquiryPage from '../pages/InquiryPage';
import ChatbotLogPage from '../pages/ChatbotLogPage';
import TestResultPage from '../pages/TestResultPage';
import ThLoggerPage from '../pages/ThLoggerPage';
import AlertCenterPage, { ALERT_LEVELS, alertCategoryMeta, alertPreview } from '../pages/AlertCenterPage';
import { getUsername, clearAuth } from '../services/authToken';

const { Header, Sider, Content } = Layout;

/* 모바일(767px 이하)에서 'PC 화면 권장' 안내를 띄우는 복잡한 화면 (2026-11-05 사용자 결정: 복잡한 화면은 모바일 대응 제외).
   표는 옆으로 밀어 볼 수 있게 두고, 배치를 모바일용으로 바꾸지 않는다.
   모바일 대응 화면: 대시보드 · 온습도로거 · 알림센터 · 파트너 문의/요청 · 게시판 */
const MOBILE_SUPPORTED = new Set(['/', '/thlogger', '/alerts', '/inquiries', '/board']);

interface TabItem {
  key: string;
  label: string;
  closable: boolean;
  icon?: React.ReactNode;
}

type MenuItem = Required<MenuProps>['items'][number];

// 메뉴코드 → 아이콘 매핑 (정적)
const ICON_MAP: Record<string, React.ReactNode> = {
  dashboard: <DashboardOutlined />,
  schedules: <CalendarOutlined />,
  protocols: <FileTextOutlined />,
  test_materials: <ExperimentOutlined />,
  master: <DatabaseOutlined />,
  monitoring: <CloudServerOutlined />,
  registry: <InboxOutlined />,
  inspections: <SafetyCertificateOutlined />,
  storage_management: <CloudOutlined />,
  server_logs: <FileTextOutlined />,
  activity_logs: <HistoryOutlined />,
  partner_accounts: <TeamOutlined />,
  inquiries: <MessageOutlined />,
  user_management: <UserOutlined />,
  role_management: <SafetyOutlined />,
  terminal: <CodeOutlined />,
  guide: <BookOutlined />,
  glossary: <ReadOutlined />,
  business_trips: <CarOutlined />,
  qr_management: <QrcodeOutlined />,
  ncpms: <BugOutlined />,
  board: <NotificationOutlined />,
  adb_demo: <DatabaseOutlined />,
  chatbot_logs: <RobotOutlined />,
  alerts: <BellOutlined />,
  ui_tests: <ExperimentOutlined />,
  thlogger: <WifiOutlined />,
};

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const username = getUsername() || 'admin';
  const { permissions, hasPermission, loaded: permLoaded, reload: reloadPermissions } = usePermission();

  // 권한 기반 동적 메뉴 생성 (DB sort_order/group_sort_order 순)
  const { menuItems, flatMenuMap } = useMemo(() => {
    const items: MenuItem[] = [];
    const fMap: Record<string, { label: string; icon: React.ReactNode }> = {};

    // permissions는 이미 group_sort_order → sort_order 순 정렬됨
    const groupOrder: string[] = [];
    const grouped: Record<string, typeof permissions> = {};
    for (const p of permissions) {
      const group = p.menu_group || '';
      if (!grouped[group]) { grouped[group] = []; groupOrder.push(group); }
      grouped[group].push(p);
    }

    let first = true;
    for (const groupName of groupOrder) {
      const perms = grouped[groupName];
      if (!perms || perms.length === 0) continue;
      if (!first) items.push({ type: 'divider' } as MenuItem);
      first = false;

      const children = perms.map(p => {
        const icon = ICON_MAP[p.menu_code] || <FileTextOutlined />;
        fMap[p.menu_path] = { label: p.menu_name, icon };
        return { key: p.menu_path, icon, label: p.menu_name };
      });

      if (groupName && groupName !== '대시보드') {
        items.push({
          key: `grp-${groupName}`,
          type: 'group',
          label: groupName,
          children,
        } as MenuItem);
      } else {
        children.forEach(c => items.push(c as MenuItem));
      }
    }

    return { menuItems: items, flatMenuMap: fMap };
  }, [permissions]);

  const [tabs, setTabs] = useState<TabItem[]>([
    { key: '/', label: '대시보드', closable: false, icon: <DashboardOutlined /> },
  ]);
  const [activeKey, setActiveKey] = useState('/');
  const [collapsed, setCollapsed] = useState(false);
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);   // 모바일 서랍 메뉴
  const [mountedKeys, setMountedKeys] = useState<string[]>(['/']);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [inquiryAlerts, setInquiryAlerts] = useState<any[]>([]);
  const [sysAlerts, setSysAlerts] = useState<any[]>([]);
  const [sysUnread, setSysUnread] = useState(0);
  const [alertDrawerOpen, setAlertDrawerOpen] = useState(false);
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [pwForm] = Form.useForm();

  const loadSystemAlerts = useCallback(() => {
    // 권한 없는 역할은 403 → 조용히 비운다 (벨은 나머지 알림만 표시)
    systemAlertAPI.summary(8)
      .then(res => { setSysAlerts(res.data.items || []); setSysUnread(res.data.unread || 0); })
      .catch(() => { setSysAlerts([]); setSysUnread(0); });
  }, []);

  useEffect(() => {
    dashboardAPI.alerts().then(res => setAlerts(res.data)).catch(() => {});
    dashboardAPI.inquiryAlerts().then(res => setInquiryAlerts(res.data)).catch(() => {});
    loadSystemAlerts();
    // 크론 알림은 접속 중에도 들어오므로 60초마다 갱신 (summary 는 가벼운 집계 쿼리)
    const timer = setInterval(loadSystemAlerts, 60000);
    return () => clearInterval(timer);
  }, [loadSystemAlerts]);

  // 파트너 문의는 답변 즉시 반영되어야 하므로 문의 탭 진입/이탈 시 재조회
  useEffect(() => {
    if (activeKey !== '/inquiries') return;
    dashboardAPI.inquiryAlerts().then(res => setInquiryAlerts(res.data)).catch(() => {});
  }, [activeKey]);

  const totalAlertCount = alerts.length + inquiryAlerts.length + sysUnread;

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const handleChangePassword = async () => {
    try {
      const values = await pwForm.validateFields();
      if (values.new_password !== values.confirm_password) {
        Modal.error({ title: '비밀번호 불일치', content: '새 비밀번호가 일치하지 않습니다.' });
        return;
      }
      await authMeAPI.changePassword({ current_password: values.current_password, new_password: values.new_password });
      Modal.success({ title: '변경 완료', content: '비밀번호가 변경되었습니다.' });
      setPwModalOpen(false);
      pwForm.resetFields();
    } catch (err: any) {
      Modal.error({ title: '변경 실패', content: err?.response?.data?.detail || '오류가 발생했습니다.' });
    }
  };

  const openTab = useCallback((key: string, label?: string) => {
    setTabs((prev) => {
      if (prev.find((t) => t.key === key)) return prev;
      const def = flatMenuMap[key];
      return [...prev, {
        key,
        label: label || def?.label || key,
        closable: key !== '/',
        icon: def?.icon,
      }];
    });
    setMountedKeys((prev) => prev.includes(key) ? prev : [...prev, key]);
    setActiveKey(key);
  }, [flatMenuMap]);

  const handleMenuClick = useCallback(({ key }: { key: string }) => {
    setNavOpen(false);
    openTab(key);
    navigate(key);
    const def = flatMenuMap[key];
    if (def) activityLogAPI.logPageView(key, def.label);
  }, [openTab, navigate, flatMenuMap]);

  const handleTabChange = useCallback((key: string) => {
    setActiveKey(key);
    navigate(key);
  }, [navigate]);

  const handleTabEdit = useCallback((targetKey: any, action: 'add' | 'remove') => {
    if (action !== 'remove') return;
    const key = targetKey as string;
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.key === key);
      const newTabs = prev.filter((t) => t.key !== key);
      if (key === activeKey && newTabs.length > 0) {
        const newActive = newTabs[Math.min(idx, newTabs.length - 1)].key;
        setActiveKey(newActive);
        navigate(newActive);
      }
      return newTabs;
    });
    setMountedKeys((prev) => prev.filter((k) => k !== key));
  }, [activeKey, navigate]);

  useEffect(() => {
    const path = location.pathname;
    if (path === '/login') return;
    if (path.startsWith('/protocols/') && path.endsWith('/edit')) {
      openTab(path, `계획서 편집 #${path.split('/')[2]}`);
    } else {
      openTab(path);
    }
  }, [location.pathname, openTab]);

  // 권한 로드 완료 후 raw path로 등록된 탭 label 갱신 (새로고침 시 flatMenuMap이 늦게 채워지는 경우 대응)
  useEffect(() => {
    if (!permLoaded) return;
    setTabs(prev => prev.map(t => {
      const def = flatMenuMap[t.key];
      if (def && t.label === t.key) {
        return { ...t, label: def.label, icon: def.icon };
      }
      return t;
    }));
  }, [permLoaded, flatMenuMap]);

  const selectedMenuKeys = (() => {
    if (activeKey.startsWith('/protocols/') && activeKey.endsWith('/edit')) return ['/protocols'];
    return [activeKey];
  })();

  const editTabKeys = mountedKeys.filter((k) => k.startsWith('/protocols/') && k.endsWith('/edit'));

  // 화면들 — 모바일 'PC 화면 권장' 화면은 PC 폭으로 그리고 옆으로 밀어 보게 감싼다(좁은 폭에 억지로 맞추면 글자가 한 자씩 세로로 깨졌다, 2026-11-05).
  // 데스크톱에서는 감싸지 않는다 — 화면테스트 러너가 '.ant-layout-content > div:visible' 로 지금 화면을 찾는다
  const pageViews = (
    <>
      <div style={{ display: activeKey === '/' ? 'block' : 'none' }}><DashboardPage /></div>
      {mountedKeys.includes('/schedules') && <div style={{ display: activeKey === '/schedules' ? 'block' : 'none' }}><SchedulePage /></div>}
      {mountedKeys.includes('/master') && <div style={{ display: activeKey === '/master' ? 'block' : 'none' }}><MasterPage /></div>}
      {mountedKeys.includes('/protocols') && <div style={{ display: activeKey === '/protocols' ? 'block' : 'none' }}><ProtocolListPage /></div>}
      {editTabKeys.map((tabKey) => <div key={tabKey} style={{ display: activeKey === tabKey ? 'block' : 'none' }}><ProtocolFormPage editId={tabKey.split('/')[2]} /></div>)}
      {mountedKeys.includes('/partner-accounts') && <div style={{ display: activeKey === '/partner-accounts' ? 'block' : 'none' }}><PartnerAccountPage /></div>}
      {mountedKeys.includes('/inquiries') && <div style={{ display: activeKey === '/inquiries' ? 'block' : 'none' }}><InquiryPage /></div>}
      {mountedKeys.includes('/monitoring') && <div style={{ display: activeKey === '/monitoring' ? 'block' : 'none' }}><MonitoringPage /></div>}
      {mountedKeys.includes('/registry') && <div style={{ display: activeKey === '/registry' ? 'block' : 'none' }}><RegistryPage /></div>}
      {mountedKeys.includes('/storage-management') && <div style={{ display: activeKey === '/storage-management' ? 'block' : 'none' }}><StoragePage /></div>}
      {mountedKeys.includes('/server-logs') && <div style={{ display: activeKey === '/server-logs' ? 'block' : 'none' }}><ServerLogPage /></div>}
      {mountedKeys.includes('/inspections') && <div style={{ display: activeKey === '/inspections' ? 'block' : 'none' }}><InspectionPage /></div>}
      {mountedKeys.includes('/activity-logs') && <div style={{ display: activeKey === '/activity-logs' ? 'block' : 'none' }}><ActivityLogPage /></div>}
      {mountedKeys.includes('/users') && <div style={{ display: activeKey === '/users' ? 'block' : 'none' }}><UserManagementPage /></div>}
      {mountedKeys.includes('/roles') && <div style={{ display: activeKey === '/roles' ? 'block' : 'none' }}><RoleManagementPage /></div>}
      {mountedKeys.includes('/terminal') && <div style={{ display: activeKey === '/terminal' ? 'block' : 'none' }}><TerminalPage /></div>}
      {mountedKeys.includes('/guide') && <div style={{ display: activeKey === '/guide' ? 'block' : 'none' }}><SystemGuidePage /></div>}
      {mountedKeys.includes('/glossary') && <div style={{ display: activeKey === '/glossary' ? 'block' : 'none' }}><GlossaryPage /></div>}
      {mountedKeys.includes('/adb-demo') && <div style={{ display: activeKey === '/adb-demo' ? 'block' : 'none' }}><AdbDemoPage /></div>}
      {mountedKeys.includes('/chatbot-logs') && <div style={{ display: activeKey === '/chatbot-logs' ? 'block' : 'none' }}><ChatbotLogPage /></div>}
      {mountedKeys.includes('/test-materials') && <div style={{ display: activeKey === '/test-materials' ? 'block' : 'none' }}><TestMaterialPage /></div>}
      {mountedKeys.includes('/business-trips') && <div style={{ display: activeKey === '/business-trips' ? 'block' : 'none' }}><BusinessTripPage /></div>}
      {mountedKeys.includes('/board') && <div style={{ display: activeKey === '/board' ? 'block' : 'none' }}><BoardPage /></div>}
      {mountedKeys.includes('/qr-management') && <div style={{ display: activeKey === '/qr-management' ? 'block' : 'none' }}><QRManagementPage /></div>}
      {mountedKeys.includes('/ncpms') && <div style={{ display: activeKey === '/ncpms' ? 'block' : 'none' }}><NcpmsPage /></div>}
      {mountedKeys.includes("/tapo") && <div style={{ display: activeKey === "/tapo" ? "block" : "none" }}><TapoManagementPage /></div>}
      {mountedKeys.includes('/alerts') && <div style={{ display: activeKey === '/alerts' ? 'block' : 'none' }}><AlertCenterPage /></div>}
      {mountedKeys.includes('/thlogger') && <div style={{ display: activeKey === '/thlogger' ? 'block' : 'none' }}><ThLoggerPage /></div>}
      {mountedKeys.includes('/ui-tests') && <div style={{ display: activeKey === '/ui-tests' ? 'block' : 'none' }}><TestResultPage /></div>}
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }} className={isMobile ? 'app-mobile' : undefined}>
      {/* ─── 모바일: 사이드바 대신 서랍 메뉴 (접힌 사이드바 48px 가 화면의 12% 를 차지했다) ─── */}
      {isMobile && (
        <Drawer open={navOpen} onClose={() => setNavOpen(false)} placement="left" width={264} closable={false}
          styles={{ body: { padding: 0, background: 'linear-gradient(180deg, #1e1b4b 0%, #272364 50%, #312e81 100%)' } }}>
          <div style={{ height: layout.headerHeight, display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px', background: 'rgba(0,0,0,0.22)' }}>
            <img src="/logo-icon.png" alt="SAPPT" style={{ width: 30, height: 30 }} />
            <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>SAPPT</div>
          </div>
          <Menu theme="dark" mode="inline" selectedKeys={selectedMenuKeys} items={menuItems} onClick={handleMenuClick} style={{ border: 'none', fontSize: 14 }} />
        </Drawer>
      )}
      {/* ─── Sidebar ─── */}
      {!isMobile && <Sider
        collapsible collapsed={collapsed} onCollapse={setCollapsed}
        breakpoint="lg" theme="dark" width={layout.sidebarWidth} collapsedWidth={layout.sidebarCollapsed}
        style={{
          background: 'linear-gradient(180deg, #1e1b4b 0%, #272364 50%, #312e81 100%)',
          borderRight: 'none',
          boxShadow: '2px 0 24px rgba(30,27,75,0.15)',
          /* 메뉴가 뷰포트보다 길어도 문서 전체가 늘어나지 않도록 사이드바를 자체 스크롤 컬럼으로 고정.
             (이 제약이 없으면 flex 형제인 Content 가 사이드바 높이까지 늘어나 카드 아래에 빈 영역이 생긴다) */
          height: '100vh',
          position: 'sticky',
          top: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
        trigger={null}
      >
        {/* Logo */}
        <div
          style={{
            /* 헤더와 같은 높이 — 사이드바 로고 줄과 본문 헤더의 밑선이 맞는다 */
            height: layout.headerHeight, display: 'flex', alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            padding: collapsed ? '0' : '0 12px',
            background: 'rgba(0,0,0,0.22)',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
          }}
          onClick={() => setCollapsed(!collapsed)}
        >
          <img
            src="/logo-icon.png"
            alt="SAPPT"
            style={{
              width: 32, height: 32, flexShrink: 0,
              marginRight: collapsed ? 0 : 10,
              filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))',
            }}
          />
          {!collapsed && (
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#ffffff', lineHeight: 1.2, letterSpacing: -0.5 }}>
                SAPPT
              </div>
              <div style={{ fontSize: 9, color: 'rgba(134,239,172,0.7)', letterSpacing: 1, textTransform: 'uppercase' as const }}>
                Pesticide Test Mgmt
              </div>
            </div>
          )}
        </div>

        {/* Menu */}
        {/* antd 인라인 메뉴 항목이 width calc(100%+2px)로 렌더링되어 가로 스크롤이 생기므로 X축은 숨김 */}
        <div style={{ padding: '4px 0', flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <Menu
            mode="inline"
            selectedKeys={selectedMenuKeys}
            items={menuItems}
            onClick={handleMenuClick}
            style={{
              border: 'none',
              fontSize: 13,
            }}
          />
        </div>

        {/* Sidebar footer */}
        {!collapsed && (
          <div style={{
            padding: '8px 12px', borderTop: '1px solid rgba(255,255,255,0.06)',
            fontSize: 10, color: 'rgba(165,180,252,0.35)', textAlign: 'center',
          }}>
            SAPPT v2.1 &middot; lab.sappt.co.kr
          </div>
        )}
      </Sider>}

      <Layout>
        {/* ─── Header ─── */}
        <Header style={{
          background: '#fff', padding: isMobile ? '0 8px 0 4px' : '0 14px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
          borderBottom: '1px solid #eef0f4', height: layout.headerHeight, lineHeight: `${layout.headerHeight}px`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        }}>
          {/* 모바일 헤더 — 메뉴 · 화면 이름 · (오른쪽) 알림 · 사용자. 시스템명 · 통합검색 · 바로가기 아이콘은 PC 에서만 */}
          {isMobile && (
            <Space size={6} align="center" style={{ minWidth: 0, flex: 1, lineHeight: 'normal' }}>
              <Button type="text" icon={<MenuOutlined style={{ fontSize: 18 }} />} onClick={() => setNavOpen(true)} aria-label="메뉴" style={{ width: 40, height: 40 }} />
              <Typography.Text strong ellipsis style={{ color: gray[800], fontSize: 15, maxWidth: 'calc(100vw - 170px)' }}>{flatMenuMap[activeKey]?.label || 'SAPPT'}</Typography.Text>
            </Space>
          )}
          {/* Left: Breadcrumb-like */}
          {!isMobile && <Space size={8}>
            <Typography.Text style={{ color: gray[400], fontSize: font.size.sm }}>
              농약 시험계획서 관리 시스템
            </Typography.Text>
            <span style={{ color: gray[200] }}>|</span>
            <Typography.Text strong style={{ color: gray[600], fontSize: font.size.sm }}>
              {flatMenuMap[activeKey]?.label || ''}
            </Typography.Text>
            <span style={{ color: '#e2e8f0', margin: '0 4px' }}>|</span>
            <GlobalSearch onNavigate={(path, label) => { openTab(path, label); navigate(path); }} />
          </Space>}

          {/* Right: Actions */}
          <Space size={8}>
            {!isMobile && <>
            <Tooltip title="시스템 가이드">
              <Button
                type="text" size="small"
                icon={<QuestionCircleOutlined style={{ color: gray[400] }} />}
                onClick={() => { openTab('/guide'); navigate('/guide'); }}
              />
            </Tooltip>
            <Tooltip title="서버 모니터링">
              <Button
                type="text" size="small"
                icon={<CloudServerOutlined style={{ color: gray[400] }} />}
                onClick={() => { openTab('/monitoring'); navigate('/monitoring'); }}
              />
            </Tooltip>
            </>}

            {/* 알림 벨 */}
            <Tooltip title={totalAlertCount > 0
              ? `미처리 알림 ${totalAlertCount}건 (시스템 ${sysUnread} · 정식시기 ${alerts.length} · 파트너문의 ${inquiryAlerts.length})`
              : '알림 없음'}>
              <Badge count={totalAlertCount} size="small" offset={[-2, 2]}>
                <Button
                  type="text" size="small"
                  icon={<BellOutlined style={{ color: totalAlertCount > 0 ? '#ef4444' : gray[400], fontSize: 15 }} />}
                  onClick={() => setAlertDrawerOpen(true)}
                />
              </Badge>
            </Tooltip>

            {!isMobile && <div style={{ width: 1, height: 20, background: '#eef0f4', margin: '0 4px' }} />}

            {/* User avatar dropdown */}
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'change-password',
                    icon: <KeyOutlined />,
                    label: '비밀번호 변경',
                    onClick: () => { pwForm.resetFields(); setPwModalOpen(true); },
                  },
                  { type: 'divider' },
                  {
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    label: '로그아웃',
                    onClick: handleLogout,
                  },
                ],
              }}
              placement="bottomRight"
            >
              <div
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: isMobile ? 4 : '5px 14px 5px 5px', borderRadius: 24,
                  background: '#f8fafc', border: '1px solid #eef0f4',
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #4338ca, #6366f1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 1px 4px rgba(99,102,241,0.3)',
                }}>
                  <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>
                    {(username || 'A')[0].toUpperCase()}
                  </span>
                </div>
                {!isMobile && <Typography.Text style={{ color: gray[700], fontSize: 13, fontWeight: 600 }}>
                  {username}
                </Typography.Text>}
              </div>
            </Dropdown>

          </Space>
        </Header>

        {/* ─── Tab Bar ─── (모바일은 화면 하나씩 — 서랍 메뉴로 이동) */}
        {!isMobile && <div style={{
          background: '#fff', borderBottom: '1px solid #eef0f4',
          paddingLeft: 8, paddingRight: 8,
        }}>
          <Tabs
            type="editable-card" hideAdd
            activeKey={activeKey} onChange={handleTabChange} onEdit={handleTabEdit}
            items={tabs.map((t) => ({
              key: t.key,
              label: (
                <span style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {t.icon && <span style={{ fontSize: 12, opacity: 0.7 }}>{t.icon}</span>}
                  {t.label}
                </span>
              ),
              closable: t.closable,
            }))}
            size="small"
            tabBarStyle={{ marginBottom: 0 }}
            tabBarGutter={4}
          />
        </div>}

        {/* ─── Content ─── */}
        <Content style={{ padding: layout.contentPadding, background: '#f4f6f9', minHeight: 'calc(100vh - 160px)' }}>
          {isMobile && !MOBILE_SUPPORTED.has(activeKey) && (
            <div className="mobile-pc-notice" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', marginBottom: 8, borderRadius: 8, background: '#eef2ff', border: '1px solid #c7d2fe', color: '#3730a3', fontSize: 13 }}>
              <DesktopOutlined /><span>이 화면은 <b>PC 화면에 맞춰져</b> 있습니다. 표는 옆으로 밀어서 볼 수 있습니다.</span>
            </div>
          )}
          {isMobile && !MOBILE_SUPPORTED.has(activeKey) ? <div className="mobile-pc-wide">{pageViews}</div> : pageViews}
        </Content>
        <ChatBot />
      </Layout>

      {/* ─── 알림 Drawer ─── */}
      <Drawer
        title={
          <Space>
            <BellOutlined style={{ color: '#ef4444' }} />
            <span style={{ fontWeight: 700 }}>알림</span>
            <Tag color="red">{totalAlertCount}건</Tag>
          </Space>
        }
        open={alertDrawerOpen}
        onClose={() => setAlertDrawerOpen(false)}
        width={480}
        placement="right"
      >
        {/* ── 시스템 알림 (백업·디스크·예찰수집 등 크론 이벤트) ── */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <BellOutlined style={{ color: brand.primary }} />
            <span style={{ fontWeight: 700, fontSize: 13 }}>시스템 알림</span>
            <Tag color={sysUnread > 0 ? 'red' : 'default'} style={{ marginLeft: 4 }}>{sysUnread}건</Tag>
            <Space size={4} style={{ marginLeft: 'auto' }}>
              {sysUnread > 0 && (
                <Button size="small" type="link" style={{ padding: 0 }}
                  onClick={async () => {
                    try { await systemAlertAPI.readAll(); } catch { /* 권한 없으면 무시 */ }
                    loadSystemAlerts();
                  }}
                >
                  모두 읽음
                </Button>
              )}
              <Button size="small" type="link" style={{ padding: 0 }}
                onClick={() => { setAlertDrawerOpen(false); openTab('/alerts'); navigate('/alerts'); }}
              >
                전체보기
              </Button>
            </Space>
          </div>
          {sysAlerts.length === 0 ? (
            <div style={{ fontSize: 12, color: gray[400], padding: '8px 0' }}>시스템 알림이 없습니다.</div>
          ) : (
            <List
              size="small"
              dataSource={sysAlerts}
              renderItem={(item: any) => {
                const lv = ALERT_LEVELS[item.level] || ALERT_LEVELS.INFO;
                const cat = alertCategoryMeta(item.category);
                return (
                  <List.Item
                    style={{
                      padding: '10px 0 10px 10px', cursor: 'pointer',
                      borderLeft: `3px solid ${item.is_read ? 'transparent' : lv.color}`,
                      background: item.is_read ? undefined : '#fafbff',
                    }}
                    onClick={async () => {
                      if (!item.is_read) {
                        try { await systemAlertAPI.read(item.id); } catch { /* noop */ }
                        loadSystemAlerts();
                      }
                      setAlertDrawerOpen(false);
                      openTab('/alerts'); navigate('/alerts');
                    }}
                  >
                    <div style={{ width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                        <Tag style={{ margin: 0, color: lv.color, background: lv.bg, borderColor: lv.bg, fontWeight: 700 }}>
                          {lv.label}
                        </Tag>
                        <span style={{ fontSize: 11, color: gray[500] }}>{cat.label}</span>
                        <span style={{
                          fontWeight: item.is_read ? 500 : 700, fontSize: 13,
                          color: item.is_read ? gray[600] : '#374151',
                        }}>{item.title}</span>
                      </div>
                      {item.message && (
                        <div style={{
                          fontSize: 11, color: gray[400], marginBottom: 2,
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          {alertPreview(item.message, item.title)}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: gray[400] }}>
                        {(item.created_at || '').replace('T', ' ').slice(0, 16)}
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />
          )}
        </div>

        {/* ── 파트너 문의/요청 (답변 대기) ── */}
        <div style={{ marginBottom: 20, paddingTop: 12, borderTop: '1px solid #eef0f4' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <MessageOutlined style={{ color: brand.primary }} />
            <span style={{ fontWeight: 700, fontSize: 13 }}>파트너 문의/요청</span>
            <Tag color={inquiryAlerts.length > 0 ? 'orange' : 'default'} style={{ marginLeft: 4 }}>
              {inquiryAlerts.length}건
            </Tag>
            {inquiryAlerts.length > 0 && (
              <Button
                size="small" type="link" style={{ marginLeft: 'auto', padding: 0 }}
                onClick={() => { setAlertDrawerOpen(false); openTab('/inquiries'); navigate('/inquiries'); }}
              >
                전체보기
              </Button>
            )}
          </div>
          {inquiryAlerts.length === 0 ? (
            <div style={{ fontSize: 12, color: gray[400], padding: '8px 0' }}>답변 대기 중인 문의가 없습니다.</div>
          ) : (
            <List
              size="small"
              dataSource={inquiryAlerts.slice(0, 10)}
              renderItem={(item: any) => (
                <List.Item
                  style={{ padding: '10px 0', cursor: 'pointer' }}
                  onClick={() => { setAlertDrawerOpen(false); openTab('/inquiries'); navigate('/inquiries'); }}
                >
                  <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                      <Tag color={item.inquiry_type === '요청' ? 'purple' : 'blue'} style={{ margin: 0, fontWeight: 600 }}>
                        {item.inquiry_type}
                      </Tag>
                      {item.company_name && <Tag color="blue" style={{ margin: 0 }}>{item.company_name}</Tag>}
                      <span style={{ fontWeight: 700, fontSize: 13, color: '#374151' }}>{item.title}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 10, fontSize: 11, color: gray[400], flexWrap: 'wrap' }}>
                      {item.test_number && <span>시험번호 <b style={{ color: brand.primary }}>{item.test_number}</b></span>}
                      <span>작성자 {item.partner_username}</span>
                      <span>{(item.created_at || '').replace('T', ' ').slice(0, 16)}</span>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, paddingTop: 12, borderTop: '1px solid #eef0f4' }}>
          <WarningOutlined style={{ color: '#ef4444' }} />
          <span style={{ fontWeight: 700, fontSize: 13 }}>정식시기 미등록</span>
          <Tag color={alerts.length > 0 ? 'red' : 'default'} style={{ marginLeft: 4 }}>{alerts.length}건</Tag>
        </div>
        {alerts.length === 0 ? (
          <div style={{ fontSize: 12, color: gray[400], padding: '8px 0' }}>
            정식시기 미등록 알림이 없습니다.
          </div>
        ) : (
          <List
            dataSource={alerts}
            renderItem={(item: any) => (
              <List.Item style={{ padding: '12px 0' }}>
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <Space size={6}>
                      {item.company_name && <Tag color="blue" style={{ fontWeight: 600 }}>{item.company_name}</Tag>}
                      <span style={{ fontWeight: 700, color: brand.primary, fontSize: 13 }}>{item.test_number}</span>
                    </Space>
                    <Tag color="red" style={{ fontSize: 11 }}>+{item.overdue_days}일 경과</Tag>
                  </div>
                  <div style={{ fontSize: 12, color: '#374151', marginBottom: 4, fontWeight: 500 }}>{item.title}</div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: gray[400] }}>
                    <span>개시일: <b style={{ color: '#ef4444' }}>{item.start_date}</b></span>
                    <span>상태: <b style={{ color: '#374151' }}>{item.status}</b></span>
                  </div>
                </div>
              </List.Item>
            )}
          />
        )}
      </Drawer>

      <Modal
        title={<><KeyOutlined style={{ marginRight: 8, color: '#6366f1' }} />비밀번호 변경</>}
        open={pwModalOpen}
        onCancel={() => setPwModalOpen(false)}
        onOk={handleChangePassword}
        okText="변경"
        width={420}
        maskClosable={false}
      >
        <Form form={pwForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="current_password" label="현재 비밀번호"
            rules={[{ required: true, message: '현재 비밀번호를 입력하세요' }]}>
            <Input.Password placeholder="현재 비밀번호" />
          </Form.Item>
          <Form.Item name="new_password" label="새 비밀번호"
            rules={[{ required: true, message: '새 비밀번호를 입력하세요' }, { min: 4, message: '4자 이상 입력하세요' }]}>
            <Input.Password placeholder="새 비밀번호 (4자 이상)" />
          </Form.Item>
          <Form.Item name="confirm_password" label="새 비밀번호 확인"
            rules={[{ required: true, message: '비밀번호를 다시 입력하세요' }]}>
            <Input.Password placeholder="새 비밀번호 확인" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default MainLayout;
